import React, { useEffect, useMemo, useState, useCallback, useRef, useLayoutEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useLanguage } from "../../../contexts/LanguageContext.js";
import api from "../../../api/client.js";
import ProductCard from "../../ProductCard/ProductCard";
import logger from "../../../utils/logger.js";
import LoadingScreen from "../../../components/ui/Loading/Loading";

import {
  SlidersHorizontal,
  Tag,
  Store,
  ShoppingBag,
  Headphones,
  Smartphone,
  Monitor,
  Zap,
  Footprints,
  User,
  Shirt,
  Code2,
  Gamepad2,
  Home,
  Search,
  ArrowRight,
  Grid3X3,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import SearchBar from "../../../components/ui/SearchBar/SearchBar";
import Pagination from "../../../components/ui/Pagination/Pagination";
import FiltersPanel from "../../ui/Filters/Filters";
import Breadcrumb from "../../../components/ui/Breadcrumb/Breadcrumb";

import "./Shop.css";

const PRODUCTS_PER_PAGE = 9;

const DEFAULT_SHOP_CATEGORIES = [
  { id: 19, slug: "accessories", name_en: "Accessories", name_ar: "إكسسوارات", Icon: Headphones },
  { id: 18, slug: "phones", name_en: "Phones & Telecom", name_ar: "الهواتف والاتصالات", Icon: Smartphone },
  { id: 17, slug: "computer", name_en: "Computer & Office", name_ar: "الكمبيوتر والمكتب", Icon: Monitor },
  { id: 16, slug: "electronics", name_en: "Electronics", name_ar: "إلكترونيات", Icon: Zap },
  { id: 15, slug: "shoes", name_en: "Shoes", name_ar: "أحذية", Icon: Footprints },
  { id: 14, slug: "men", name_en: "Men's Clothing", name_ar: "ملابس رجالية", Icon: User },
  { id: 13, slug: "women", name_en: "Women's Clothing", name_ar: "ملابس نسائية", Icon: Shirt },
  { id: 11, slug: "programming", name_en: "Programming", name_ar: "برمجة", Icon: Code2 },
  { id: 10, slug: "digital", name_en: "Digital Products", name_ar: "منتجات رقمية", Icon: Gamepad2 },
  { id: 8, slug: "housewares", name_en: "Housewares", name_ar: "أدوات منزلية", Icon: Home },
  { id: 7, slug: "clothing", name_en: "Clothing", name_ar: "ملابس", Icon: ShoppingBag },
];

function getIconForCategory(categoryId) {
  const iconMap = {
    19: Headphones, 18: Smartphone, 17: Monitor, 16: Zap, 15: Footprints,
    14: User, 13: Shirt, 11: Code2, 10: Gamepad2, 8: Home, 7: ShoppingBag,
  };
  return iconMap[categoryId] || Store;
}

function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function HighlightedText({ text, query }) {
  if (!query || !query.trim()) return <span>{text}</span>;
  const escapedQuery = escapeRegExp(query);
  const parts = text.split(new RegExp(`(${escapedQuery})`, "gi"));
  return (
    <span>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <span key={i} className="search-highlight">{part}</span>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </span>
  );
}

function SkeletonCard() {
  return (
    <div className="skeleton-card">
      <div className="skeleton skeleton-image" />
      <div className="skeleton skeleton-title" />
      <div className="skeleton skeleton-price" />
      <div className="skeleton skeleton-button" />
    </div>
  );
}

function SkeletonGrid({ count }) {
  return (
    <div className="products-grid">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
}

function normalizeFilterValue(value) {
  return String(value ?? "").trim().toLowerCase();
}

function getFilterOptionId(option) {
  return normalizeFilterValue(option?.id ?? option?.value ?? option?.slug ?? option);
}

function getFilterOptionLabel(option, isAr = false) {
  return String(
    isAr
      ? option?.label_ar ?? option?.label_en ?? option?.slug ?? option?.id ?? ""
      : option?.label_en ?? option?.label_ar ?? option?.slug ?? option?.id ?? ""
  ).trim();
}

function sanitizeFilterOptions(options = []) {
  const seen = new Set();
  const normalizedOptions = [];

  options.forEach((option) => {
    const id = getFilterOptionId(option);
    if (!id || seen.has(id)) return;
    seen.add(id);
    normalizedOptions.push({
      id,
      slug: String(option?.slug ?? ""),
      label_ar: String(option?.label_ar ?? option?.label_en ?? option?.slug ?? option?.id ?? "").trim(),
      label_en: String(option?.label_en ?? option?.label_ar ?? option?.slug ?? option?.id ?? "").trim(),
    });
  });

  return normalizedOptions.sort((a, b) =>
    getFilterOptionLabel(a, false).localeCompare(getFilterOptionLabel(b, false), undefined, {
      sensitivity: "base",
    })
  );
}

function buildFallbackFilterOptions(products, groupKey, legacyKey) {
  const optionsMap = new Map();

  products.forEach((product) => {
    const labels = Array.isArray(product?.filter_option_labels?.[groupKey])
      ? product.filter_option_labels[groupKey]
      : [];

    if (labels.length > 0) {
      labels.forEach((option) => {
        const id = getFilterOptionId(option);
        if (!id || optionsMap.has(id)) return;
        optionsMap.set(id, {
          id,
          slug: String(option?.slug ?? ""),
          label_ar: String(option?.label_ar ?? option?.label_en ?? option?.slug ?? option?.id ?? "").trim(),
          label_en: String(option?.label_en ?? option?.label_ar ?? option?.slug ?? option?.id ?? "").trim(),
        });
      });
      return;
    }

    const legacyValue = String(product?.[legacyKey] ?? "").trim();
    const id = normalizeFilterValue(legacyValue);
    if (!id || optionsMap.has(id)) return;

    optionsMap.set(id, {
      id,
      slug: id,
      label_ar: legacyValue,
      label_en: legacyValue,
    });
  });

  return sanitizeFilterOptions(Array.from(optionsMap.values()));
}

function isValidFilterOption(options, value) {
  if (value === "all") return true;
  const normalizedValue = getFilterOptionId(value);
  return options.some((option) => getFilterOptionId(option) === normalizedValue);
}

function resolveProductFilterIds(product, groupKey, legacyKey) {
  const explicitIds = Array.isArray(product?.filter_option_ids?.[groupKey])
    ? product.filter_option_ids[groupKey]
    : [];

  const normalizedIds = explicitIds
    .map((value) => getFilterOptionId(value))
    .filter(Boolean);

  if (normalizedIds.length > 0) {
    return normalizedIds;
  }

  const legacyValue = normalizeFilterValue(product?.[legacyKey]);
  return legacyValue ? [legacyValue] : [];
}

function productMatchesFilterOption(product, groupKey, legacyKey, selectedValue) {
  if (!selectedValue || selectedValue === "all") return true;
  const selectedId = getFilterOptionId(selectedValue);
  return resolveProductFilterIds(product, groupKey, legacyKey).includes(selectedId);
}

function getProductCurrentPrice(product) {
  const finalPrice = Number(
    product?.final_price ?? product?.sale_price ?? product?.price ?? 0
  );
  const basePrice = Number(product?.price ?? 0);
  return finalPrice > 0 ? finalPrice : basePrice;
}

function getProductOriginalPrice(product) {
  return Number(
    product?.original_price ?? product?.old_price ?? product?.price ?? 0
  );
}

function hasProductDiscount(product) {
  const originalPrice = getProductOriginalPrice(product);
  const currentPrice = getProductCurrentPrice(product);
  return (
    product?.has_discount === true ||
    (originalPrice > 0 && currentPrice > 0 && currentPrice < originalPrice)
  );
}

function getProductRating(product) {
  return Number(product?.average_rating ?? product?.rating ?? 0);
}

function getProductSalesScore(product) {
  return Number(
    product?.sales_count ??
      product?.sold_count ??
      product?.orders_count ??
      product?.total_sales ??
      product?.reviews_count ??
      0
  );
}

function isProductInStock(product) {
  if (Number(product?.stock ?? 0) > 0) return true;
  if (Array.isArray(product?.variants)) {
    if (product.variants.some((variant) => Number(variant?.stock ?? 0) > 0)) {
      return true;
    }
  }
  return product?.is_digital === true;
}

function filterProductList(products, criteria) {
  let result = [...products];

  if (criteria.category && criteria.category !== "all") {
    result = result.filter((p) => String(p.category_id || "") === criteria.category);
  }

  if (criteria.sort === "offers") {
    result = result.filter((p) => hasProductDiscount(p));
  }

  if (criteria.color && criteria.color !== "all") {
    result = result.filter((p) =>
      productMatchesFilterOption(p, "colors", "color", criteria.color)
    );
  }
  if (criteria.size && criteria.size !== "all") {
    result = result.filter((p) =>
      productMatchesFilterOption(p, "sizes", "size", criteria.size)
    );
  }
  if (criteria.type && criteria.type !== "all") {
    result = result.filter((p) =>
      productMatchesFilterOption(p, "types", "type", criteria.type)
    );
  }

  if (criteria.rating && criteria.rating > 0) {
    result = result.filter((p) => getProductRating(p) >= criteria.rating);
  }

  if (criteria.inStock) {
    result = result.filter((p) => isProductInStock(p));
  }

  if (criteria.price !== undefined && criteria.price !== null) {
    result = result.filter((p) => getProductCurrentPrice(p) <= Number(criteria.price));
  }

  if (criteria.searchQuery && criteria.searchQuery.trim()) {
    const q = criteria.searchQuery.toLowerCase();
    result = result.filter((p) => {
      const name = criteria.isAr
        ? (p.name_ar || p.name_en || "").toLowerCase()
        : (p.name_en || p.name || "").toLowerCase();
      return name.includes(q);
    });
  }

  if (criteria.sort === "new_arrivals") {
    result.sort((a, b) => {
      const dA = a.created_at ? new Date(a.created_at) : 0;
      const dB = b.created_at ? new Date(b.created_at) : 0;
      return dB - dA || (b.id || 0) - (a.id || 0);
    });
  } else if (criteria.sort === "best_rated") {
    result.sort((a, b) => {
      const ratingDiff = getProductRating(b) - getProductRating(a);
      if (ratingDiff !== 0) return ratingDiff;
      return (b.reviews_count || 0) - (a.reviews_count || 0);
    });
  } else if (criteria.sort === "best_sellers") {
    result.sort((a, b) => {
      const salesDiff = getProductSalesScore(b) - getProductSalesScore(a);
      if (salesDiff !== 0) return salesDiff;
      return getProductRating(b) - getProductRating(a);
    });
  }

  return result;
}

export default function Shop() {
  const { lang, t } = useLanguage();
  const navigate = useNavigate();
  const isAr = lang === "ar";
  const emptyFilterCatalog = useMemo(
    () => ({ colors: [], sizes: [], types: [] }),
    []
  );

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_SHOP_CATEGORIES);
  const [filterCatalog, setFilterCatalog] = useState(emptyFilterCatalog);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");

  const [activeCategory, setActiveCategory] = useState("all");
  const [activeSort, setActiveSort] = useState(null);
  const [activeRating, setActiveRating] = useState(0);
  const [activeInStock, setActiveInStock] = useState(false);

  const [filters, setFilters] = useState({
    color: "all",
    size: "all",
    type: "all",
    price: 0,
  });

  const [panelFilters, setPanelFilters] = useState(filters);
  const [panelSort, setPanelSort] = useState(null);
  const [panelRating, setPanelRating] = useState(0);
  const [panelInStock, setPanelInStock] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const searchWrapperRef = useRef(null);
  const tabsScrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(isAr);
  const [canScrollRight, setCanScrollRight] = useState(!isAr);

  const [currentPage, setCurrentPage] = useState(1);
  const [visibleCount, setVisibleCount] = useState(PRODUCTS_PER_PAGE);
  const [isMobile, setIsMobile] = useState(false);

  const maxPrice = useMemo(() => {
    if (!products.length) return 0;
    return Math.max(...products.map((p) => getProductCurrentPrice(p)));
  }, [products]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const handler = (e) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadCategories() {
      try {
        const res = await api.apiGet("categories");
        if (cancelled) return;
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res?.categories) ? res.categories : [];
        const categoriesWithIcons = list
          .filter(cat => cat.is_active === 1 || cat.is_active === true)
          .map(cat => ({
            id: cat.id,
            slug: cat.slug || `cat-${cat.id}`,
            name_en: cat.name_en || cat.name || "",
            name_ar: cat.name_ar || "",
            Icon: getIconForCategory(cat.id),
          }))
          .sort((a, b) => (a.name_en || "").localeCompare(b.name_en || ""));

        if (categoriesWithIcons.length > 0) {
          setCategories(categoriesWithIcons);
        }
      } catch (e) {
        logger.warn("Error loading categories, using defaults", e);
      }
    }
    loadCategories();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        setMsg("");
        const res = await api.apiGet("products");
        if (cancelled) return;
        const list = Array.isArray(res?.products) ? res.products : [];
        const serverFilters =
          res?.filters && typeof res.filters === "object" ? res.filters : emptyFilterCatalog;
        setProducts(list);
        setFilterCatalog({
          colors: Array.isArray(serverFilters.colors) ? serverFilters.colors : [],
          sizes: Array.isArray(serverFilters.sizes) ? serverFilters.sizes : [],
          types: Array.isArray(serverFilters.types) ? serverFilters.types : [],
        });
        const prices = list.map((p) => getProductCurrentPrice(p));
        const maxP = prices.length ? Math.max(...prices) : 0;
        const defaults = { color: "all", size: "all", type: "all", price: maxP };
        setFilters(defaults);
        setPanelFilters(defaults);
      } catch (e) {
        logger.error("Error loading shop data", e);
        setMsg(t("shop_server_error", isAr ? "فشل الاتصال بالسيرفر" : "Connection failed"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [lang, t, isAr, emptyFilterCatalog]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 250);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
    setVisibleCount(PRODUCTS_PER_PAGE);
  }, [activeCategory, activeSort, activeRating, activeInStock, filters, debouncedQuery]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target)) {
        setShowSearchDropdown(false);
      }
    }
    if (showSearchDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showSearchDropdown]);

  useEffect(() => {
    function handleEscape(e) {
      if (e.key === "Escape") setShowSearchDropdown(false);
    }
    if (showSearchDropdown) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [showSearchDropdown]);

  const colors = useMemo(
    () =>
      sanitizeFilterOptions(
        filterCatalog.colors.length > 0
          ? filterCatalog.colors
          : buildFallbackFilterOptions(products, "colors", "color")
      ),
    [filterCatalog.colors, products]
  );
  const sizes = useMemo(
    () =>
      sanitizeFilterOptions(
        filterCatalog.sizes.length > 0
          ? filterCatalog.sizes
          : buildFallbackFilterOptions(products, "sizes", "size")
      ),
    [filterCatalog.sizes, products]
  );
  const types = useMemo(
    () =>
      sanitizeFilterOptions(
        filterCatalog.types.length > 0
          ? filterCatalog.types
          : buildFallbackFilterOptions(products, "types", "type")
      ),
    [filterCatalog.types, products]
  );

  useEffect(() => {
    setFilters((prev) => {
      const next = { ...prev };
      let changed = false;

      if (!isValidFilterOption(colors, prev.color)) {
        next.color = "all";
        changed = true;
      }
      if (!isValidFilterOption(sizes, prev.size)) {
        next.size = "all";
        changed = true;
      }
      if (!isValidFilterOption(types, prev.type)) {
        next.type = "all";
        changed = true;
      }

      return changed ? next : prev;
    });
  }, [colors, sizes, types]);

  useEffect(() => {
    setPanelFilters((prev) => {
      const next = { ...prev };
      let changed = false;

      if (!isValidFilterOption(colors, prev.color)) {
        next.color = "all";
        changed = true;
      }
      if (!isValidFilterOption(sizes, prev.size)) {
        next.size = "all";
        changed = true;
      }
      if (!isValidFilterOption(types, prev.type)) {
        next.type = "all";
        changed = true;
      }

      return changed ? next : prev;
    });
  }, [colors, sizes, types]);

  const filtered = useMemo(() => {
    return filterProductList(products, {
      category: activeCategory,
      sort: activeSort,
      searchQuery: debouncedQuery,
      color: filters.color,
      size: filters.size,
      type: filters.type,
      price: filters.price,
      rating: activeRating,
      inStock: activeInStock,
      isAr,
    });
  }, [products, activeCategory, activeSort, debouncedQuery, filters, activeRating, activeInStock, isAr]);

  const previewFiltered = useMemo(() => {
    return filterProductList(products, {
      category: activeCategory,
      sort: panelSort,
      searchQuery: debouncedQuery,
      color: panelFilters.color,
      size: panelFilters.size,
      type: panelFilters.type,
      price: panelFilters.price,
      rating: panelRating,
      inStock: panelInStock,
      isAr,
    });
  }, [products, activeCategory, panelSort, debouncedQuery, panelFilters, panelRating, panelInStock, isAr]);

  const searchSuggestions = useMemo(() => {
    const emptyResult = { products: [], categories: [] };
    if (!debouncedQuery || !debouncedQuery.trim()) return emptyResult;
    const q = debouncedQuery.toLowerCase();
    const matchingProducts = products.filter((p) => {
      const name = isAr
        ? (p.name_ar || p.name_en || "").toLowerCase()
        : (p.name_en || p.name || "").toLowerCase();
      return name.includes(q);
    }).slice(0, 5);
    const matchingCategories = categories.filter((cat) => {
      const name = (isAr ? cat.name_ar : cat.name_en || "").toLowerCase();
      return name.includes(q) && !matchingProducts.some(p => String(p.category_id) === String(cat.id));
    }).slice(0, 2);
    return { products: matchingProducts, categories: matchingCategories };
  }, [products, categories, debouncedQuery, isAr]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filtered.length / PRODUCTS_PER_PAGE)),
    [filtered]
  );
  const currentPageSafe = Math.min(currentPage, totalPages);

  const displayedProducts = useMemo(() => {
    if (isMobile) {
      return filtered.slice(0, visibleCount);
    }
    const start = (currentPageSafe - 1) * PRODUCTS_PER_PAGE;
    return filtered.slice(start, start + PRODUCTS_PER_PAGE);
  }, [filtered, isMobile, visibleCount, currentPageSafe]);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.color !== "all") count++;
    if (filters.size !== "all") count++;
    if (filters.type !== "all") count++;
    if (filters.price < maxPrice) count++;
    if (activeRating > 0) count++;
    if (activeInStock) count++;
    if (activeSort) count++;
    return count;
  }, [filters, maxPrice, activeRating, activeInStock, activeSort]);

  const priceChips = useMemo(() => {
    const chips = [{ label: isAr ? "الكل" : "All", value: maxPrice }];
    const thresholds = [50, 100, 200, 500, 1000];
    thresholds.forEach((v) => {
      if (maxPrice > v) chips.push({ label: `$${v}`, value: v });
    });
    return chips;
  }, [maxPrice, isAr]);

  const hasActiveFilters = useMemo(() => {
    return (
      activeCategory !== "all" ||
      (debouncedQuery && debouncedQuery.trim()) ||
      activeSort ||
      activeRating > 0 ||
      activeInStock ||
      filters.color !== "all" ||
      filters.size !== "all" ||
      filters.type !== "all" ||
      (filters.price !== undefined && filters.price < maxPrice)
    );
  }, [activeCategory, debouncedQuery, activeSort, activeRating, activeInStock, filters, maxPrice]);

  const handleOpenFilters = useCallback(() => {
    setPanelFilters(filters);
    setPanelSort(activeSort);
    setPanelRating(activeRating);
    setPanelInStock(activeInStock);
    setFiltersOpen(true);
  }, [filters, activeSort, activeRating, activeInStock]);

  const handleApplyFilters = useCallback(() => {
    setFilters(panelFilters);
    setActiveSort(panelSort);
    setActiveRating(panelRating);
    setActiveInStock(panelInStock);
    setFiltersOpen(false);
    setShowSearchDropdown(false);
  }, [panelFilters, panelSort, panelRating, panelInStock]);

  const handlePanelReset = useCallback(() => {
    const defaults = { color: "all", size: "all", type: "all", price: maxPrice };
    setPanelFilters(defaults);
    setPanelSort(null);
    setPanelRating(0);
    setPanelInStock(false);
   
    setFilters(defaults);
    setActiveSort(null);
    setActiveRating(0);
    setActiveInStock(false);
  }, [maxPrice]);

  const handleResetFilters = useCallback(() => {
    const defaults = { color: "all", size: "all", type: "all", price: maxPrice };
    setFilters(defaults);
    setPanelFilters(defaults);
    setActiveCategory("all");
    setActiveSort(null);
    setActiveRating(0);
    setActiveInStock(false);
    setPanelSort(null);
    setPanelRating(0);
    setPanelInStock(false);
    setSearchQuery("");
    setDebouncedQuery("");
    setShowSearchDropdown(false);
  }, [maxPrice]);

  const handleSearchSubmit = useCallback((query) => {
    setSearchQuery(query);
    setDebouncedQuery(query);
    setShowSearchDropdown(false);
    const grid = document.querySelector(".products-grid");
    if (grid) grid.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const handleTapAhead = useCallback((query, e) => {
    if (e) e.stopPropagation();
    setSearchQuery(query);
    setShowSearchDropdown(true);
  }, []);

  const handleViewAllResults = useCallback(() => {
    setShowSearchDropdown(false);
    const grid = document.querySelector(".products-grid");
    if (grid) grid.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleLoadMore = useCallback(() => {
    setVisibleCount((prev) => prev + PRODUCTS_PER_PAGE);
  }, []);

  const updateScrollState = useCallback(() => {
    const el = tabsScrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScroll = scrollWidth - clientWidth;
    const absScrollLeft = Math.abs(scrollLeft);
    const hasOverflow = scrollWidth > clientWidth + 4;
    setCanScrollLeft(absScrollLeft > 4);
    setCanScrollRight(hasOverflow && absScrollLeft < maxScroll - 4);
  }, []);

  useLayoutEffect(() => {
    const el = tabsScrollRef.current;
    if (!el) return;

    updateScrollState();
    requestAnimationFrame(() => {
      updateScrollState();
      requestAnimationFrame(updateScrollState);
    });

    el.addEventListener("scroll", updateScrollState, { passive: true });

    const ro = new ResizeObserver(() => {
      requestAnimationFrame(updateScrollState);
    });
    ro.observe(el);

    window.addEventListener("resize", updateScrollState);

    const id1 = setTimeout(updateScrollState, 100);
    const id2 = setTimeout(updateScrollState, 400);

    return () => {
      clearTimeout(id1);
      clearTimeout(id2);
      window.removeEventListener("resize", updateScrollState);
      el.removeEventListener("scroll", updateScrollState);
      ro.disconnect();
    };
  }, [updateScrollState]);

  useEffect(() => {
    updateScrollState();
  }, [activeCategory, updateScrollState]);

  useEffect(() => {
    const el = tabsScrollRef.current;
    if (el) {
      el.scrollLeft = 0;
      requestAnimationFrame(() => {
        setCanScrollLeft(isAr);
        setCanScrollRight(!isAr);
        updateScrollState();
      });
    }
  }, [isAr, updateScrollState]);

  const scrollTabs = useCallback((direction) => {
    const el = tabsScrollRef.current;
    if (!el) return;
    const amount = Math.round(el.clientWidth * 0.6);
    const before = el.scrollLeft;
    el.scrollBy({ left: direction === "right" ? amount : -amount, behavior: "smooth" });

    requestAnimationFrame(() => {
      if (el.scrollLeft === before) {
        if (direction === "right") {
          setCanScrollLeft(true);
          setCanScrollRight(false);
        } else {
          setCanScrollLeft(false);
          setCanScrollRight(true);
        }
      } else {
        updateScrollState();
        let last = el.scrollLeft;
        const track = () => {
          requestAnimationFrame(() => {
            updateScrollState();
            if (Math.abs(el.scrollLeft - last) > 0.5) {
              last = el.scrollLeft;
              track();
            }
          });
        };
        track();
      }
    });
  }, [updateScrollState]);

  const categoryTabs = useMemo(() => {
    const tabs = [{ key: "all", label: isAr ? "الكل" : "All", Icon: ShoppingBag }];
    categories.forEach((cat) => {
      tabs.push({
        key: String(cat.id),
        label: isAr ? cat.name_ar : cat.name_en,
        Icon: cat.Icon,
      });
    });
    return tabs;
  }, [isAr, categories]);

  const emptyTitle = useMemo(() => {
    if (debouncedQuery && debouncedQuery.trim()) {
      return isAr
        ? `لا توجد نتائج لـ "${debouncedQuery.trim()}"`
        : `No results for "${debouncedQuery.trim()}"`;
    }
    return isAr ? "لا توجد منتجات مطابقة" : "No matching products";
  }, [debouncedQuery, isAr]);

  const emptySubtitle = useMemo(() => {
    if (debouncedQuery && debouncedQuery.trim()) {
      return isAr
        ? "جرّب كلمة بحث مختلفة أو أعد ضبط الفلاتر"
        : "Try a different search term or reset filters";
    }
    return isAr
      ? "جرّب تغيير الفلاتر أو إعادة ضبطها"
      : "Try changing the filters or reset them";
  }, [debouncedQuery, isAr]);

  const breadcrumbItems = useMemo(() => {
    const items = [];

    items.push({
      label: isAr ? "المتجر" : "Shop",
      link: "/shop",
    });

    if (activeCategory !== "all") {
      const cat = categoryTabs.find((t) => t.key === activeCategory);
      if (cat) {
        items.push({ label: cat.label });
      }
    }
    else if (debouncedQuery && debouncedQuery.trim()) {
      items.push({
        label: isAr
          ? `بحث: "${debouncedQuery.trim()}"`
          : `Search: "${debouncedQuery.trim()}"`,
      });
    }
    else if (hasActiveFilters) {
      items.push({
        label: isAr ? "نتائج مفلترة" : "Filtered Results",
      });
    }

    return items;
  }, [isAr, activeCategory, debouncedQuery, hasActiveFilters, categoryTabs]);

  if (loading) {
    return (
      <div className="shop-page" dir={isAr ? "rtl" : "ltr"}>
        <div className="container py-4">
          <LoadingScreen isAr={isAr} />
        </div>
      </div>
    );
  }

  const hasProductSuggestions = searchSuggestions.products && searchSuggestions.products.length > 0;
  const hasCategorySuggestions = searchSuggestions.categories && searchSuggestions.categories.length > 0;
  const hasSuggestions = hasProductSuggestions || hasCategorySuggestions;

  return (
    <div className="shop-page" dir={isAr ? "rtl" : "ltr"}>
      <div className="shop-breadcrumb">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="shop-header">
        <div className="container">
          <div className="shop-header-content">
            <div className="shop-title-wrapper">
              <div className="shop-icon">
                <Store size={28} />
              </div>
              <div className="shop-title-group">
                <h1 className="shop-title">{isAr ? "المتجر" : "Shop"}</h1>
              </div>
            </div>

            <div className="shop-header-actions">
              <div className="search-dropdown-wrapper" ref={searchWrapperRef}>
                <SearchBar
                  value={searchQuery}
                  onChange={(val) => {
                    setSearchQuery(val);
                    setShowSearchDropdown(val.trim().length > 0);
                  }}
                  onSubmit={handleSearchSubmit}
                  enableDropdown={false}
                  placeholder={isAr ? "ابحث في الهواتف، الأحذية، الملابس..." : "Search phones, shoes, clothing..."}
                />
                {showSearchDropdown && debouncedQuery && debouncedQuery.trim() && (
                  <div className="search-dropdown searchbar-dropdown">
                    {!hasSuggestions ? (
                      <div className="search-dropdown-empty">
                        {isAr ? "لا توجد نتائج" : "No results found"}
                      </div>
                    ) : (
                      <>
                        {hasProductSuggestions && searchSuggestions.products.map((product) => {
                          const displayName = isAr
                            ? product.name_ar || product.name_en
                            : product.name_en || product.name;
                          return (
                            <div
                              key={`prod-${product.id}`}
                              className="search-dropdown-item"
                              onClick={() => { setShowSearchDropdown(false); navigate(`/product/${product.id}`); }}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  setShowSearchDropdown(false);
                                  navigate(`/product/${product.id}`);
                                }
                              }}
                            >
                              <Search size={12} className="search-dropdown-icon" />
                              <div className="search-dropdown-info">
                                <div className="search-dropdown-name">
                                  <HighlightedText text={displayName} query={debouncedQuery} />
                                </div>
                              </div>
                              <button
                                className="tap-ahead-btn"
                                onClick={(e) => handleTapAhead(displayName, e)}
                                aria-label={isAr ? "إكمال البحث" : "Complete search"}
                                title={isAr ? "إكمال البحث" : "Complete search"}
                              >
                                <ArrowRight size={12} style={{ transform: isAr ? "scaleX(-1)" : "none" }} />
                              </button>
                            </div>
                          );
                        })}

                        {hasCategorySuggestions && searchSuggestions.categories.map((cat) => {
                          const displayName = isAr ? cat.name_ar : cat.name_en;
                          return (
                            <div
                              key={`cat-${cat.id}`}
                              className="search-dropdown-item"
                              onClick={() => {
                                setActiveCategory(String(cat.id));
                                setSearchQuery("");
                                setDebouncedQuery("");
                                setShowSearchDropdown(false);
                              }}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  setActiveCategory(String(cat.id));
                                  setSearchQuery("");
                                  setDebouncedQuery("");
                                  setShowSearchDropdown(false);
                                }
                              }}
                            >
                              <Search size={12} className="search-dropdown-icon" />
                              <div className="search-dropdown-info">
                                <div className="search-dropdown-name">
                                  <HighlightedText text={displayName} query={debouncedQuery} />
                                  <span style={{ color: "var(--text-muted)", fontSize: "10px", marginLeft: isAr ? "0" : "4px", marginRight: isAr ? "4px" : "0" }}>
                                    {isAr ? "في الفئة" : "in category"}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}

                        <div className="search-dropdown-footer">
                          <button onClick={handleViewAllResults}>
                            {isAr
                              ? `عرض كل النتائج (${filtered.length})`
                              : `View all results (${filtered.length})`}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
              <Link
                to="/offers"
                className="offers-link-btn"
                aria-label={isAr ? "العروض" : "Offers"}
              >
                <Tag size={18} />
                <span>{isAr ? "العروض" : "Offers"}</span>
              </Link>

              <button
                className="filter-toggle-btn"
                onClick={handleOpenFilters}
                aria-expanded={filtersOpen}
                aria-controls="filters-panel"
              >
                <SlidersHorizontal size={18} />
                <span>{isAr ? "الفلاتر" : "Filters"}</span>
                {activeFiltersCount > 0 && (
                  <span className="filter-toggle-badge">{activeFiltersCount}</span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <FiltersPanel
        isAr={isAr}
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        panelFilters={panelFilters}
        setPanelFilters={setPanelFilters}
        panelSort={panelSort}
        setPanelSort={setPanelSort}
        panelRating={panelRating}
        setPanelRating={setPanelRating}
        panelInStock={panelInStock}
        setPanelInStock={setPanelInStock}
        colors={colors}
        sizes={sizes}
        types={types}
        maxPrice={maxPrice}
        priceChips={priceChips}
        previewCount={previewFiltered.length}
        onApply={handleApplyFilters}
        onReset={handlePanelReset}
      />

      <div className="shop-tabs-bar">
        <div className="container">
          <div className="shop-tabs-track">
            <button
              className={`tabs-arrow tabs-arrow-left ${(isAr ? canScrollRight : canScrollLeft) ? "visible" : ""}`}
              onClick={() => scrollTabs(isAr ? "right" : "left")}
              aria-label={isAr ? "تمرير للأمام" : "Scroll left"}
              tabIndex={(isAr ? canScrollRight : canScrollLeft) ? 0 : -1}
            >
              {isAr ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </button>

            <div
              className={`shop-tabs-scroll
                ${canScrollLeft  ? "fade-left"  : ""}
                ${canScrollRight ? "fade-right" : ""}
              `}
              ref={tabsScrollRef}
            >
              <div className="shop-tabs-list" role="tablist">
                {categoryTabs.map((tab) => {
                  const Icon = tab.Icon;
                  const isActive = activeCategory === tab.key;
                  return (
                    <button
                      key={tab.key}
                      role="tab"
                      aria-selected={isActive}
                      className={`shop-tab-item ${isActive ? "active" : ""}`}
                      onClick={() => setActiveCategory(tab.key)}
                    >
                      <span className="tab-icon">
                        <Icon size={16} />
                      </span>
                      <span className="tab-label">{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              className={`tabs-arrow tabs-arrow-right ${(isAr ? canScrollLeft : canScrollRight) ? "visible" : ""}`}
              onClick={() => scrollTabs(isAr ? "left" : "right")}
              aria-label={isAr ? "تمرير للخلف" : "Scroll right"}
              tabIndex={(isAr ? canScrollLeft : canScrollRight) ? 0 : -1}
            >
              {isAr ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
            </button>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="results-bar" dir={isAr ? "rtl" : "ltr"}>
          <div className="results-bar-inner">
            {activeCategory !== "all" ? (
              <div className="results-category-row">
                <span className="results-category-name">
                  {categoryTabs.find(t => t.key === activeCategory)?.label}
                </span>
                <span className="results-count-badge" key={filtered.length}>
                  {filtered.length}
                  <span className="results-count-badge-label">
                    {isAr ? (filtered.length === 1 ? "منتج" : "منتجات") : (filtered.length === 1 ? "product" : "products")}
                  </span>
                </span>
              </div>
            ) : hasActiveFilters ? (
              <>
                <span className="results-count-number" key={filtered.length}>
                  {filtered.length}
                </span>
                <span className="results-suffix">
                  {isAr ? (filtered.length === 1 ? "منتج" : "منتجات") : (filtered.length === 1 ? "product" : "products")}
                </span>
              </>
            ) : (
              <span className="results-all-label">
                {isAr ? "جميع المنتجات" : "All Products"}
              </span>
            )}
          </div>
          {hasActiveFilters && <span className="results-accent" />}
        </div>
      </div>

      <div className="container">
        {msg && (
          <div className="shop-alert">
            <p>{msg}</p>
          </div>
        )}

        {filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <Grid3X3 size={48} />
            </div>
            <h3>{emptyTitle}</h3>
            <p>{emptySubtitle}</p>
            <button className="reset-btn-large" onClick={handleResetFilters}>
              {isAr ? "إعادة ضبط الفلاتر" : "Reset Filters"}
            </button>
          </div>
        ) : (
          <>
            <div className="products-grid">
              {displayedProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>

            <div className="pagination-desktop">
              <Pagination
                currentPage={currentPageSafe}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>

            {isMobile && filtered.length > displayedProducts.length && (
              <div className="load-more-wrapper">
                <button className="load-more-btn" onClick={handleLoadMore}>
                  {isAr ? "تحميل المزيد" : "Load More"}
                  <span className="load-more-count">
                    ({displayedProducts.length} / {filtered.length})
                  </span>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}